import { z } from "zod";

export const leadActivityMessageSchema = z.object({
  createdAt: z.string(),
  content: z.string(),
  type: z.enum(["text", "audio", "image", "video", "document"]),
  source: z.enum([
    "customer",
    "ai_agent",
    "human_agent",
    "tool_call",
    "tool_response",
  ]),
  received: z.boolean(),
});

export type LeadActivityMessageDelta = z.infer<typeof leadActivityMessageSchema>;

export const leadActivitySchema = z.object({
  leadId: z.string(),
  conversationId: z.string(),
  message: leadActivityMessageSchema.optional(),
});

export type LeadActivityEvent = z.infer<typeof leadActivitySchema>;

export const webSocketMessageSchema = z.object({
  type: z.literal("leadActivity"),
  data: leadActivitySchema,
});

export type WebSocketMessage = z.infer<typeof webSocketMessageSchema>;

type WebSocketClientConfig = {
  url: string;
  getToken: () => string | undefined;
  onMessage?: (message: WebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
};

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private config: WebSocketClientConfig;
  private reconnectAttempts = 0;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private isIntentionalClose = false;

  constructor(config: WebSocketClientConfig) {
    this.config = {
      reconnectInterval: 3000,
      maxReconnectAttempts: 10,
      ...config,
    };
  }

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) return;

    this.isIntentionalClose = false;

    const token = this.config.getToken();
    if (!token) return;

    const wsUrl = `${this.config.url}?token=${encodeURIComponent(token)}`;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
        this.config.onConnect?.();
      };

      this.ws.onmessage = (event) => {
        try {
          const message = webSocketMessageSchema.parse(JSON.parse(event.data));
          this.config.onMessage?.(message);
        } catch (error) {
          console.error("Failed to parse WebSocket message:", error);
        }
      };

      this.ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        this.config.onError?.(error);
      };

      this.ws.onclose = () => {
        this.config.onDisconnect?.();

        if (!this.isIntentionalClose) {
          this.attemptReconnect();
        }
      };
    } catch (error) {
      console.error("Failed to create WebSocket connection:", error);
      this.attemptReconnect();
    }
  }

  disconnect(): void {
    this.isIntentionalClose = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CLOSING) {
        this.ws.close();
      } else if (this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.onopen = null;
        this.ws.onmessage = null;
        this.ws.onerror = null;
        this.ws.onclose = null;
      }
      this.ws = null;
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= (this.config.maxReconnectAttempts || 10)) return;

    this.reconnectAttempts++;

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, this.config.reconnectInterval);
  }

  getReadyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}
